/**
 * Rendering one tensor's analysis.
 *
 * Charts here are hand-rolled SVG rather than a charting library: the marks are
 * simple (bars, rules, dots) and the data-viz constraints this project works
 * under — thin marks, recessive grid, direct labels, a table alternative — are
 * easier to satisfy directly than to talk a library out of its defaults.
 *
 * **The table view is not an afterthought.** Three of the light-mode series
 * colours sit below 3:1 against white, which obliges any chart using them to
 * carry visible labels or a table. It also happens to be what a research
 * audience actually wants — the numbers, copyable — so it ships alongside every
 * chart rather than behind an accessibility toggle.
 */

import type { ReadPlan } from "../hf/read-plan.js";
import type { InventoryTensor } from "../safetensors/inventory.js";
import type { AnalysisResult } from "../worker/protocol.js";
import { cardSummaryTitle, cardTitle } from "./card.js";
import { type ExplainKey, explainIcon } from "./explain.js";
import { formatBytes, formatParams, formatShape } from "./format.js";

const CHART_WIDTH = 880;
const CHART_HEIGHT = 200;
const CHART_PAD = 44;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Four significant figures is the resolution these statistics actually carry. */
function sig(value: number | undefined, digits = 4): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return Number(value).toPrecision(digits);
}

export interface TensorViewModel {
  readonly tensor: InventoryTensor;
  readonly plan: ReadPlan;
  readonly result: AnalysisResult;
  readonly logScale: boolean;
  /**
   * The what-if sweep is running right now, over these same results.
   *
   * Set while the pane is kept up during a recompute, so the card that was
   * asked to do the work says it is doing it. Without this the button stays
   * live under a spinner in the banner above, and pressing it again queues a
   * second eighteen-pass sweep for the answer already on its way.
   */
  readonly whatIfRunning?: boolean;
  /** The 48-pass clip-grid sweep is running right now. Same contract. */
  readonly clipGridRunning?: boolean;
}

export function renderTensorAnalysis(model: TensorViewModel): string {
  const { tensor, plan, result } = model;
  return [
    `<h2 class="mono tensor-title">${escapeHtml(tensor.name)}</h2>`,
    renderSamplingNotice(plan, result),
    renderDequantBanner(result),
    renderStats(tensor, result, plan),
    renderStructureWarning(result),
    renderHistogramCard(model),
    renderPlanesCard(result),
    renderTernaryCard(result, model.clipGridRunning === true),
    renderWhatIfCard(result, model.whatIfRunning === true),
    renderTableCard(tensor, result, plan),
  ].join("");
}

/**
 * Say plainly when the numbers came from a sample.
 *
 * A σ from a sample and a σ from a whole tensor are different claims, and the
 * UI may never blur them. `max|w|` gets its own sentence because "sampled" is
 * too weak a word for it: a sample's maximum is a *lower bound* on the true
 * maximum, and for a tool whose subject is outliers that distinction is the
 * whole point.
 */
function renderSamplingNotice(plan: ReadPlan, result: AnalysisResult): string {
  if (plan.mode === "full") return "";
  const percent = ((plan.sampledRows / plan.totalRows) * 100).toFixed(1);
  return `<div class="notice notice--info">
    <strong>Sampled.</strong> This tensor is larger than the auto-fetch limit, so
    ${plan.sampledRows.toLocaleString()} of ${plan.totalRows.toLocaleString()} rows
    (${percent}%) were read, as ${plan.ranges.length} whole-row runs at offsets derived
    from the commit hash — so this link always shows you exactly this sample.
    Statistics below are estimates from those rows.
    <strong>max|w| is a floor, not a maximum</strong>: the largest value in a sample can
    only understate the largest value in the tensor.
    ${result.spread?.cols === undefined ? "Column-norm spread is omitted entirely — a column norm needs every row, and from a sample it would be a confident-looking guess." : ""}
  </div>`;
}

function renderStats(
  tensor: InventoryTensor,
  result: AnalysisResult,
  plan: ReadPlan,
): string {
  const m = result.moments;
  const sampled = plan.mode === "sampled";
  const cells: Array<[string, string, ExplainKey]> = [
    ["shape", formatShape(tensor.shape), "shape"],
    ["dtype", tensor.dtype, "dtype"],
    ["params", formatParams(tensor.numel), "params"],
    ["bytes", formatBytes(tensor.byteLength), "bytes"],
    ["σ (RMS)", sig(m.sigma), "sigma"],
    ["mean |w|", sig(m.meanAbs), "mean-abs"],
    [sampled ? "max |w| (floor)" : "max |w|", sig(m.maxAbs), "max-abs"],
    ["kurtosis", m.kurtosis === undefined ? "—" : sig(m.kurtosis, 3), "kurtosis"],
    ["sparsity", `${(m.sparsity * 100).toFixed(2)}%`, "sparsity"],
  ];
  if (result.spread?.rows.ratio !== undefined) {
    cells.push(["row norm p99÷p50", sig(result.spread.rows.ratio, 3), "row-spread"]);
  }
  if (result.spread?.cols?.ratio !== undefined) {
    cells.push(["col norm p99÷p50", sig(result.spread.cols.ratio, 3), "col-spread"]);
  }
  if (m.nonFinite > 0) {
    cells.push(["non-finite", m.nonFinite.toLocaleString(), "non-finite"]);
  }

  return `<div class="card"><dl class="stat-grid">${cells
    .map(
      ([label, value, key]) =>
        `<div><dt>${escapeHtml(label)}${explainIcon(key)}</dt><dd class="mono">${escapeHtml(value)}</dd></div>`,
    )
    .join("")}</dl></div>`;
}

/**
 * The structure warning — the seed tool's headline finding, and the reason the
 * sampling design goes to such lengths to keep row norms exact.
 */
function renderStructureWarning(result: AnalysisResult): string {
  const ratio = result.spread?.rows.ratio;
  if (ratio === undefined || ratio <= 8) return "";
  return `<div class="notice notice--warn">
    <strong>Row-structured tensor.</strong> The p99 row norm is ${sig(ratio, 3)}× the median.
    A single per-tensor scale will zero the median rows and clip the hot ones — finer scale
    granularity (per-group or per-row) is the first fix, before any convention debate.
  </div>`;
}

function renderHistogramCard(model: TensorViewModel): string {
  const { histogram } = model.result;
  if (!histogram) {
    return `<div class="card"><p class="prose prose--muted">No distribution to draw — every
      value is zero or non-finite.</p></div>`;
  }

  const counts = Array.from(histogram.counts);
  const maxCount = histogram.maxCount || 1;
  const baseline = CHART_HEIGHT - 26;
  const plotTop = 16;
  const scaleY = (count: number) => {
    const fraction = model.logScale
      ? Math.log10(1 + count) / Math.log10(1 + maxCount)
      : count / maxCount;
    return baseline - fraction * (baseline - plotTop);
  };
  const barWidth = (CHART_WIDTH - 2 * CHART_PAD) / counts.length;

  const bars = counts
    .map((count, index) => {
      if (count === 0) return "";
      const x = CHART_PAD + index * barWidth;
      const y = scaleY(count);
      return `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${Math.max(1, barWidth - 1).toFixed(2)}" height="${(baseline - y).toFixed(2)}" rx="1" fill="var(--series-1)"/>`;
    })
    .join("");

  // Out-of-range values are the tail — the thing this whole tool is about — so
  // they are stated, not silently dropped the way the prototype did.
  const outside =
    histogram.outsideRange > 0
      ? `<p class="chart-note"><strong>${histogram.outsideRange.toLocaleString()}</strong> values
         (${((histogram.outsideRange / model.result.analyzed) * 100).toFixed(3)}%) fall outside
         ±4.5σ and are not drawn — ${histogram.belowRange.toLocaleString()} below,
         ${histogram.aboveRange.toLocaleString()} above. That tail is the interesting part.</p>`
      : `<p class="chart-note">Every value falls within ±4.5σ.</p>`;

  return `<div class="card">
    ${cardTitle(
      `Weight distribution
      <button class="btn btn--tiny" id="log-toggle" type="button" aria-pressed="${model.logScale}">log y</button>`,
    )}
    <svg viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" role="img"
         aria-label="Histogram of weight values between ${sig(histogram.lo, 3)} and ${sig(histogram.hi, 3)}">
      <line x1="${CHART_PAD}" x2="${CHART_WIDTH - CHART_PAD}" y1="${baseline}" y2="${baseline}" stroke="var(--axis)"/>
      ${bars}
      <text x="${CHART_PAD}" y="${CHART_HEIGHT - 8}" font-size="10" fill="var(--muted)">${sig(histogram.lo, 3)}</text>
      <text x="${CHART_WIDTH / 2}" y="${CHART_HEIGHT - 8}" font-size="10" fill="var(--muted)" text-anchor="middle">0</text>
      <text x="${CHART_WIDTH - CHART_PAD}" y="${CHART_HEIGHT - 8}" font-size="10" fill="var(--muted)" text-anchor="end">${sig(histogram.hi, 3)}</text>
    </svg>
    ${outside}
  </div>`;
}

/**
 * The ternary-scale comparison, ported from the seed tool.
 *
 * The clip-grid row carries a warning because the metric it optimizes is one a
 * single outlier can dominate: relF is norm-relative, so on a heavy-tailed
 * tensor the sweep will happily pick a scale that reproduces the outlier and
 * zeroes almost everything else, scoring *better* than absmean while destroying
 * the tensor. Showing the number without the caveat would actively mislead.
 */
/**
 * What the clip-grid sweep is, kept in every state of the card.
 *
 * Same rule as the what-if blurb: a card that answers a question should not
 * delete the question, and this paragraph carries the caveat that makes the
 * clip-grid row readable at all.
 */
const CLIP_GRID_BLURB = `<p class="chart-note">The <strong>clip-grid</strong> sweep is not run by
      default — it is 48 full passes over the tensor (measured at 97% of total analysis time) and it
      is the least trustworthy of the three, since relF is norm-relative and the sweep can score
      well by reproducing outliers and deleting everything else.</p>`;

/** Addressable, so progress can be patched in without re-rendering the pane. */
export const CLIP_GRID_PROGRESS_ID = "clipgrid-progress";

function renderTernaryCard(result: AnalysisResult, running: boolean): string {
  const t = result.ternary;
  if (!t) return "";
  const rows: Array<[string, { scale: number; relF: number; zeroFraction: number }]> = [
    ["absmean", t.absmean],
    ["Lloyd–Max", t.lloydMax],
  ];
  if (t.clipGrid) rows.push(["clip-grid", t.clipGrid]);

  const suspiciousClipGrid = (t.clipGrid?.zeroFraction ?? 0) > 0.9;

  return `<div class="card">
    ${cardTitle("Ternary quantization, three scale conventions")}
    <table class="data-table">
      <thead><tr>
        <th>convention</th>
        <th>scale${explainIcon("ternary-scale")}</th>
        <th>relF${explainIcon("relf")}</th>
        <th>p₀ (zeroed)${explainIcon("zero-fraction")}</th>
      </tr></thead>
      <tbody>${rows
        .map(
          ([name, scheme]) => `<tr>
          <td>${escapeHtml(name)}</td>
          <td class="mono">${sig(scheme.scale)}</td>
          <td class="mono">${sig(scheme.relF, 3)}</td>
          <td class="mono">${(scheme.zeroFraction * 100).toFixed(1)}%</td>
        </tr>`,
        )
        .join("")}</tbody>
    </table>
    ${CLIP_GRID_BLURB}
    ${
      running && !t.clipGrid
        ? /*
           * Running: the button becomes a spinner *here*, in the card that was
           * asked to do the work. It had no running state at all, so clicking
           * "run the clip-grid sweep" left this card visually identical for the
           * length of a 48-pass sweep while a banner elsewhere claimed work was
           * happening (owner, 2026-08-10).
           */
          `<p class="card-action working" id="${CLIP_GRID_PROGRESS_ID}">${escapeHtml(clipGridProgressText())}</p>`
        : t.clipGrid
          ? ""
          : `<p class="card-action"><button class="btn btn--tiny" id="clipgrid-toggle" type="button">run the clip-grid sweep</button></p>`
    }
    ${
      suspiciousClipGrid && t.clipGrid
        ? `<p class="chart-note chart-note--warn">The clip-grid sweep zeroed
           ${(t.clipGrid.zeroFraction * 100).toFixed(1)}% of this tensor. relF is norm-relative,
           so on a heavy-tailed tensor the sweep can score well by reproducing the outliers and
           deleting everything else. Read that row as a diagnostic, not a recommendation.</p>`
        : ""
    }
  </div>`;
}

/**
 * Say plainly that these numbers describe *reconstructed* weights.
 *
 * A quantized tensor's stored bytes are packed integers; the distribution worth
 * looking at is what the model actually runs on, which only exists after
 * dequantization. Presenting that without saying so would let a reader think
 * they were seeing the file's contents.
 */
function renderDequantBanner(result: AnalysisResult): string {
  const d = result.dequantized;
  if (!d) return "";
  // Measured or trusted? The stored shapes fix only the product bits × group,
  // so several schemes fit the same file. Saying which kind of claim this is
  // costs one clause and is the difference between a fact and a repetition.
  const measured = d.evidence === "anchor" || d.evidence === "anchor-propagated";
  const provenance = measured
    ? `Derived from the model's own dimensions — the shapes alone fix only the product of bits and group size, so this split was <strong>measured</strong>, not read off the config.`
    : d.evidence === "declaration"
      ? `Taken from the repo's <code>config.json</code>: the stored shapes fix only the product of bits and group size, so this split could not be confirmed independently.`
      : "";

  return `<div class="notice notice--info">
    <strong>Dequantized.</strong> This tensor is stored as ${d.bits}-bit affine-quantized integers in
    groups of ${d.groupSize}; every statistic below describes the <em>reconstructed</em> weights, not
    the packed bytes. Real cost is <strong>${d.effectiveBitsPerWeight.toFixed(2)} bits per weight</strong>
    once the scale and bias planes are counted — a "${d.bits}-bit" model is not ${d.bits} bits.
    ${provenance}
  </div>`;
}

/**
 * The scale and bias planes, in their own right.
 *
 * The plan calls these "a stat nobody surfaces, and often where quantization
 * sickness shows", and it is right: the weights can look fine while the scales
 * tell you a handful of groups are being asked to cover a huge dynamic range,
 * which is exactly where reconstruction error concentrates. They cost nothing
 * extra — the planes had to be fetched to dequantize at all.
 */
function renderPlanesCard(result: AnalysisResult): string {
  const d = result.dequantized;
  if (!d) return "";

  const plane = (label: string, report: typeof d.scales) => `
    <div class="plane">
      <div class="plane-title">${escapeHtml(label)} <span class="plane-count">${report.count.toLocaleString()} values</span></div>
      <dl class="stat-grid stat-grid--tight">
        ${stat("σ", sig(report.moments.sigma), "sigma")}
        ${stat("mean", sig(report.moments.mean), "mean")}
        ${stat("max |·|", sig(report.moments.maxAbs), "max-abs")}
        ${stat("kurtosis", report.moments.kurtosis === undefined ? "—" : sig(report.moments.kurtosis, 3), "kurtosis")}
      </dl>
      ${miniHistogram(report.histogram)}
    </div>`;

  // MLX anchors each group on whichever extreme has the larger magnitude and
  // flips the scale's sign to suit, so a healthy repo sits near 50%. Far from
  // it is worth noticing — and an implementation that assumed non-negative
  // scales would mirror half the tensor and still look plausible.
  const negatives = (d.negativeScaleFraction * 100).toFixed(1);

  return `<div class="card">
    ${cardTitle("Scale and bias planes")}
    <div class="planes">${plane("scales", d.scales)}${plane("biases", d.biases)}</div>
    <p class="chart-note"><strong>${negatives}%</strong> of scales are negative. MLX stores a
    <em>bias</em>, not a zero-point, and anchors each group on whichever extreme has the larger
    magnitude — so around half is normal here, and a number far from it is worth a second look.
    A long tail in the scales means a few groups are covering a much wider dynamic range than the
    rest, which is where reconstruction error concentrates.</p>
  </div>`;
}

/** One labelled figure in a stat grid. */
function stat(label: string, value: string, key: ExplainKey): string {
  return `<div>
    <dt>${escapeHtml(label)}${explainIcon(key)}</dt>
    <dd class="mono">${escapeHtml(value)}</dd>
  </div>`;
}

/** A compact histogram for the plane cards — same marks, less furniture. */
function miniHistogram(histogram: AnalysisResult["histogram"]): string {
  if (!histogram)
    return `<p class="chart-note">No spread to draw — every value is identical.</p>`;
  const counts = Array.from(histogram.counts);
  const maxCount = histogram.maxCount || 1;
  const width = 380;
  const height = 56;
  const barWidth = width / counts.length;
  const bars = counts
    .map((count, index) => {
      if (count === 0) return "";
      const h = (count / maxCount) * height;
      return `<rect x="${(index * barWidth).toFixed(2)}" y="${(height - h).toFixed(2)}" width="${Math.max(1, barWidth - 0.5).toFixed(2)}" height="${h.toFixed(2)}" fill="var(--series-1)"/>`;
    })
    .join("");
  return `<svg viewBox="0 0 ${width} ${height}" class="mini-hist" role="img"
    aria-label="Distribution from ${sig(histogram.lo, 3)} to ${sig(histogram.hi, 3)}">${bars}</svg>
    <div class="mini-hist-axis mono"><span>${sig(histogram.lo, 3)}</span><span>${sig(histogram.hi, 3)}</span></div>`;
}

/**
 * The what-if grid: what k-bit affine at group g would cost *this* tensor.
 *
 * relF and mangledFraction are shown together, always, and the order matters.
 * relF is norm-relative, so on a heavy-tailed tensor a configuration can score
 * a better relF than a sane one while replacing half the elements outright —
 * measured on a synthetic case at 28x "better" with 49% of weights destroyed,
 * and with the relF curve bit-identical from 2 to 8 bits because a single
 * outlier owns the group's range at every width. Showing relF alone would
 * actively recommend the destructive configuration.
 *
 * So the cells are coloured by mangledFraction — the share of elements whose
 * own relative error exceeds a threshold, deliberately unweighted by magnitude
 * and therefore impossible to buy off by preserving outliers — with relF
 * printed alongside rather than encoded.
 */
/**
 * What the sweep is, in one paragraph.
 *
 * Kept in **every** state of the card, including after the table arrives. It
 * used to be replaced by the results, which threw away the only explanation of
 * what the numbers mean at the moment someone first has numbers to read
 * (owner, 2026-08-10). A card that answers a question should not delete the
 * question.
 */
const WHAT_IF_BLURB = `<p class="prose prose--muted">Simulate k-bit affine quantization across the
      (bits x group) grid and see what each setting would cost this specific tensor.
      It is 21 cells over three full passes of the weights, so it runs only when
      asked, and only for the tensor you asked it for.</p>`;

/**
 * The id the progress line is patched through.
 *
 * Ticks arrive about ten times a second, and `renderView` replaces the whole
 * pane's `innerHTML` — so re-rendering per tick would rebuild every chart in
 * the view, drop scroll position and blow away focus, several times a second,
 * to change one number. The line is addressable so the tick can write to it
 * directly.
 */
export const WHAT_IF_PROGRESS_ID = "whatif-progress";

/** The clip-grid line's text. Passes are atomic there, so no percentage. */
export function clipGridProgressText(progress?: {
  readonly pass: number;
  readonly passes: number;
}): string {
  if (!progress) return "Running the clip-grid sweep — 48 passes over the weights…";
  return `Clip-grid pass ${progress.pass} of ${progress.passes} over the weights`;
}

/** The line's own text, so the initial render and every tick agree on wording. */
export function whatIfProgressText(progress?: {
  readonly pass: number;
  readonly passes: number;
  readonly fraction: number;
}): string {
  if (!progress) return "Pass 1 of 3 over the weights…";
  const percent = Math.min(99, Math.max(0, Math.round(progress.fraction * 100)));
  return `Pass ${progress.pass} of ${progress.passes} over the weights — ${percent}%`;
}

function renderWhatIfCard(result: AnalysisResult, running: boolean): string {
  /*
   * A card spins **only while it has nothing to show**.
   *
   * Two reports got this to its final shape (owner, 2026-08-10). First: the
   * running branch returned early, so switching on the clip-grid sweep blanked
   * an already-computed what-if table. Then, with the line made additive: a
   * spinner reappeared under the *ternary* card when the what-if sweep was
   * started, because both sweeps ride on one request and both flags were on.
   *
   * Both sweeps genuinely do re-run — but a clip-grid table that is about to be
   * recomputed from the same bytes will come back identical, so a spinner over
   * it says "these numbers may change" about numbers that cannot. The banner
   * above the pane already says work is in progress; that is the honest place
   * for it.
   *
   * The rule: results, once present, sit still. A card announces work only when
   * the work is the reason it is empty.
   */
  const progress =
    running && !result.whatIf
      ? `<p class="card-action working" id="${WHAT_IF_PROGRESS_ID}">${escapeHtml(whatIfProgressText())}</p>`
      : "";

  if (running && !result.whatIf) {
    // Nothing to show yet: the spinner stands where the button was.
    return `<div class="card">
      ${cardTitle("What if this were quantized?")}
      ${WHAT_IF_BLURB}
      ${progress}
    </div>`;
  }

  if (!result.whatIf) {
    return `<div class="card">
      ${cardTitle("What if this were quantized?")}
      ${WHAT_IF_BLURB}
      <!--
        The button is a sibling of the paragraph, not the last word in it. Inline,
        it landed mid-line after "weights." and read as part of the sentence
        rather than as the thing the sentence is asking you to press.

        The card-action wrapper is shared with the clip-grid card's button and
        with both progress lines, so the four sit at the same distance from the
        paragraph above them. They did not: this one inherited the default p
        margin and the other one chart-note's 8px, a visible mismatch between two
        cards stacked directly on top of each other (owner, 2026-08-10).
      -->
      <p class="card-action"><button class="btn btn--tiny" id="whatif-toggle" type="button">run the what-if sweep</button></p>
    </div>`;
  }

  const bitsList = [...new Set(result.whatIf.map((c) => c.bits))].sort((a, b) => a - b);
  const groups = [...new Set(result.whatIf.map((c) => c.groupSize))].sort(
    (a, b) => a - b,
  );
  const byKey = new Map(result.whatIf.map((c) => [`${c.bits}/${c.groupSize}`, c]));

  const rows = groups
    .map((g) => {
      const cells = bitsList
        .map((b) => {
          const cell = byKey.get(`${b}/${g}`);
          if (!cell) return "<td>—</td>";
          // 9-step ramp on the fraction of elements actually destroyed.
          const step = Math.min(
            9,
            Math.max(1, Math.ceil(cell.mangledFraction * 9) || 1),
          );
          return `<td class="whatif-cell" style="background:var(--ramp-${step})">
            <span class="whatif-relf">${cell.relF.toExponential(2)}</span>
            <span class="whatif-mangled">${(cell.mangledFraction * 100).toFixed(1)}% mangled</span>
            <span class="whatif-bpw">${cell.effectiveBitsPerWeight.toFixed(2)} bpw</span>
          </td>`;
        })
        .join("");
      return `<tr><th scope="row">group ${g}</th>${cells}</tr>`;
    })
    .join("");

  return `<div class="card">
    ${cardTitle("What if this were quantized?")}
    ${WHAT_IF_BLURB}
    <div class="whatif-scroll">
      <table class="data-table whatif-table">
        <thead><tr><th></th>${bitsList
          .map((b) => `<th scope="col">${b}-bit</th>`)
          .join("")}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="chart-note">Cells are shaded by <strong>share of weights mangled</strong> — the
    fraction of weights whose own relative error exceeds a threshold, counted per weight and
    deliberately not weighted by magnitude, so it cannot be bought by preserving the large ones.
    <strong>relF</strong> — relative Frobenius error, the reconstruction error as a fraction of the
    original — is reported beside it but is not what the colour means: relF is norm-relative, so a
    setting can score a better relF than a sane one while replacing half the tensor. Effective
    bits-per-weight includes the per-group scale and bias planes, which is why 4-bit at group 32
    costs 5.00.</p>
  </div>`;
}

/**
 * Every number on this page, in one copyable table.
 *
 * Discharges the palette's relief obligation for the chart above, and doubles
 * as the "let me paste these into my own notes" affordance a research audience
 * will reach for anyway.
 */
function renderTableCard(
  tensor: InventoryTensor,
  result: AnalysisResult,
  plan: ReadPlan,
): string {
  const m = result.moments;
  const rows: Array<[string, string]> = [
    ["tensor", tensor.name],
    ["shape", formatShape(tensor.shape)],
    ["dtype", tensor.dtype],
    ["parameters", String(tensor.numel)],
    ["bytes", String(tensor.byteLength)],
    ["read mode", plan.mode],
    ["rows analyzed", `${plan.sampledRows} of ${plan.totalRows}`],
    ["elements analyzed", String(result.analyzed)],
    ["sigma (RMS)", String(m.sigma)],
    ["mean", String(m.mean)],
    ["mean |w|", String(m.meanAbs)],
    [plan.mode === "sampled" ? "max |w| (lower bound)" : "max |w|", String(m.maxAbs)],
    [
      "kurtosis (about zero)",
      m.kurtosis === undefined ? "undefined" : String(m.kurtosis),
    ],
    ["sparsity", String(m.sparsity)],
    ["non-finite excluded", String(m.nonFinite)],
  ];
  if (result.spread) {
    rows.push(["row norm p50", String(result.spread.rows.p50)]);
    rows.push(["row norm p99", String(result.spread.rows.p99)]);
    rows.push(["row norm p99/p50", String(result.spread.rows.ratio ?? "undefined")]);
    rows.push([
      "col norm p99/p50",
      result.spread.cols === undefined
        ? "suppressed (sampled read)"
        : String(result.spread.cols.ratio ?? "undefined"),
    ]);
  }
  rows.push(["worker time (ms)", result.elapsedMs.toFixed(1)]);

  return `<details class="card">
    ${cardSummaryTitle("All values, as a table")}
    <table class="data-table data-table--kv">
      <tbody>${rows
        .map(
          ([key, value]) =>
            `<tr><th scope="row">${escapeHtml(key)}</th><td class="mono">${escapeHtml(value)}</td></tr>`,
        )
        .join("")}</tbody>
    </table>
  </details>`;
}
